// Reads the attribute catalog and the user's encrypted attribute rows. Encryption/
// decryption itself happens in VaultContext (which holds the in-memory master key);
// this service only moves ciphertext + definitions.

import { supabase } from './supabaseClient';
import { AttributeDataType, AttributeDefinition, AttributeValue, StoredAttribute } from './zkVault/types';

// A COARSE, non-sensitive summary stored in value_summary (plaintext) so attributes
// can be matched/searched without decrypting encrypted_details. Free text is never
// summarized (unbounded / too identifying). Used only in aggregate, suppressed RPCs.
export const summarizeAttributeValue = (
  dataType: AttributeDataType,
  value: AttributeValue,
): string | null => {
  if (value === null || value === undefined || value === '') return null;
  switch (dataType) {
    case 'BOOLEAN':
      return value ? 'yes' : 'no';
    case 'NUMBER': {
      const n = Number(value);
      if (!isFinite(n)) return null;
      const lo = Math.floor(n / 10) * 10;
      return `${lo}-${lo + 9}`; // decade bucket, e.g. 30-39
    }
    case 'SELECTION':
      return String(value); // already coarse
    default:
      return null; // TEXT / IMAGE not indexed
  }
};

export interface AttributeFilter { attribute_id: string; value: string; }

// Privacy-preserving reach: count of users matching ALL filters (server suppresses
// small counts). Lets researchers estimate audience without seeing identities.
export const fetchAttributeReach = async (filters: AttributeFilter[]): Promise<number> => {
  if (filters.length === 0) return 0;
  const { data, error } = await supabase.rpc('attribute_reach', { p_filters: filters });
  if (error) { console.error('reach failed', error); return 0; }
  return (data as number) ?? 0;
};

export const fetchAttributeDefinitions = async (): Promise<AttributeDefinition[]> => {
  const { data, error } = await supabase
    .from('user_attribute_definitions')
    .select('id, category, label_en, label_kh, data_type, requires_verification, verification_reward')
    .order('category', { ascending: true });
  if (error) throw error;
  return (data as AttributeDefinition[]) || [];
};

export const fetchMyAttributes = async (): Promise<StoredAttribute[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('user_attributes')
    .select('attribute_id, encrypted_details, encrypted_symmetric_key, value_summary, is_verified')
    .eq('user_id', user.id);
  if (error) throw error;
  return (data as StoredAttribute[]) || [];
};

export const upsertAttribute = async (row: StoredAttribute): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { error } = await supabase
    .from('user_attributes')
    .upsert({
      user_id: user.id,
      attribute_id: row.attribute_id,
      encrypted_details: row.encrypted_details,
      encrypted_symmetric_key: row.encrypted_symmetric_key,
      value_summary: row.value_summary,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,attribute_id' });
  if (error) throw error;
};

export const deleteAttribute = async (attributeId: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { error } = await supabase
    .from('user_attributes')
    .delete()
    .eq('user_id', user.id)
    .eq('attribute_id', attributeId);
  if (error) throw error;
};
